// src/components/VideoSection.jsx - COMPLETELY REDESIGNED
import React, { useState, useRef, useEffect } from 'react';

var isGJ = false;

const VideoSection = () => {
  const domain = window.location.hostname;
  if (domain.includes("gojuris.ai")) {
    isGJ = true;
  }

  const [showVideo, setShowVideo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('fade-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const handleVideoClick = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    
    setTimeout(() => {
      setShowVideo(true);
      setIsLoading(false);
    }, 800);
  };

  const handleCloseVideo = () => {
    setShowVideo(false);
  };

  return (
    <section className="modern-video-section" ref={sectionRef} id="video-section">
      <div className="container">
        {/* Section Header */}
        <div className="video-header">
          <h2 className="video-title">
            See How <span className="gradient-text">{isGJ ? 'LJSoft Online' : 'LJSoft Online'} AI</span> Works
          </h2>
          <p className="video-subtitle">
            Watch our quick demo and discover how AI transforms legal research into a faster, smarter experience
          </p>
        </div>

        {/* Video Container */}
        <div className="video-container-modern">
          {!showVideo ? (
            <div className="video-thumbnail">
              {/* Decorative Elements */}
              <div className="video-decoration decoration-1"></div>
              <div className="video-decoration decoration-2"></div>
              <div className="video-decoration decoration-3"></div>

              {/* Thumbnail Content */}
              <div className="thumbnail-overlay"></div>
              <div className="thumbnail-content">
                <div className="play-button-wrapper">
                  <button
                    onClick={handleVideoClick}
                    className="play-button"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <div className="spinner"></div>
                    ) : (
                      <i className="bx bx-play"></i>
                    )}
                  </button>
                  <div className="pulse-ring"></div>
                  <div className="pulse-ring-2"></div>
                </div>
                
                <div className="thumbnail-text">
                  <h3>Experience the Power LJSoft Online </h3>
                  <p>2:30 min demo</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="video-player">
              <div className="video-wrapper">
                <iframe
                  src="https://www.youtube.com/embed/7eyL7SSJUkM?autoplay=1&rel=0&modestbranding=1"
                  title="AI Legal Research Demo"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                ></iframe>
              </div>
              <button
                onClick={handleCloseVideo}
                className="close-video-btn"
              >
                <i className="bx bx-x"></i>
              </button>
            </div>
          )}
        </div>

        {/* Video Features */}
        <div className="video-features">
          <div className="video-feature">
            <i className="bx bx-time"></i>
            <span>Quick Tutorial</span>
          </div>
          <div className="feature-divider"></div>
          <div className="video-feature">
            <i className="bx bx-brain"></i>
            <span>Fast and Reliable</span>
          </div>
          <div className="feature-divider"></div>
          <div className="video-feature">
            <i className="bx bx-bulb"></i>
            <span>Easy to Use</span>
          </div>
        </div>
      </div>

      <style jsx>{`
        /* ============================================
           MODERN VIDEO SECTION
        ============================================ */
        .modern-video-section {
          padding: 80px 0;
          background: linear-gradient(180deg, #ffffff 0%, #fef5f0 100%);
          position: relative;
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.8s ease;
        }

        .modern-video-section.fade-in {
          opacity: 1;
          transform: translateY(0);
        }

        /* Section Header */
        .video-header {
          text-align: center;
          margin-bottom: 48px;
        }

        .video-title {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 800;
          color: #1a1a1a;
          margin-bottom: 16px;
          letter-spacing: -0.02em;
        }

        .video-subtitle {
          font-size: clamp(1rem, 2vw, 1.15rem);
          color: #666;
          max-width: 700px;
          margin: 0 auto;
        }

        /* Video Container */
        .video-container-modern {
          position: relative;
          border-radius: 24px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
          margin-bottom: 40px;
        }

        /* Video Thumbnail */
        .video-thumbnail {
          position: relative;
          width: 100%;
          height: 500px;
          background: linear-gradient(135deg, #E91E63 0%, #E91E64 50%, #c41849 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }

        /* Decorative Elements */
        .video-decoration {
          position: absolute;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
        }

        .decoration-1 {
          width: 300px;
          height: 300px;
          top: -100px;
          right: -100px;
          animation: float 20s ease-in-out infinite;
        }

        .decoration-2 {
          width: 200px;
          height: 200px;
          bottom: -50px;
          left: -50px;
          animation: float 15s ease-in-out infinite reverse;
        }

        .decoration-3 {
          width: 150px;
          height: 150px;
          top: 50%;
          left: 10%;
          animation: float 25s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% {
            transform: translate(0, 0) scale(1);
          }
          33% {
            transform: translate(30px, -30px) scale(1.1);
          }
          66% {
            transform: translate(-30px, 30px) scale(0.9);
          }
        }

        .thumbnail-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.2);
        }

        /* Thumbnail Content */
        .thumbnail-content {
          position: relative;
          z-index: 1;
          text-align: center;
        }

        /* Play Button */
        .play-button-wrapper {
          position: relative;
          margin-bottom: 24px;
        }

        .play-button {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          background: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
        }

        .play-button:hover {
          transform: scale(1.1);
        }

        .play-button i {
          font-size: 40px;
          color: #c41849;
          margin-left: 5px;
        }

        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid rgba(233, 30, 99, 0.2);
          border-top-color: #c41849;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .pulse-ring,
        .pulse-ring-2 {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 100px;
          height: 100px;
          border-radius: 50%;
          border: 3px solid white;
          animation: pulse 2s ease-out infinite;
        }

        .pulse-ring-2 {
          animation-delay: 1s;
        }

        @keyframes pulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          100% {
            transform: translate(-50%, -50%) scale(1.8);
            opacity: 0;
          }
        }

        /* Thumbnail Text */
        .thumbnail-text {
          color: white;
        }

        .thumbnail-text h3 {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 8px;
          text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
        }

        .thumbnail-text p {
          font-size: 16px;
          opacity: 0.9;
          margin: 0;
        }

        /* Video Player */
        .video-player {
          position: relative;
        }

        .video-wrapper {
          position: relative;
          padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
          height: 0;
          overflow: hidden;
        }

        .video-wrapper iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          border: none;
        }

        .close-video-btn {
          position: absolute;
          top: 20px;
          right: 20px;
          width: 44px;
          height: 44px;
          border-radius: 50%;
          background: white;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
          z-index: 100;
        }

        .close-video-btn:hover {
          transform: scale(1.1);
          background: #c41849;
        }

        .close-video-btn i {
          font-size: 24px;
          color: #333;
        }

        .close-video-btn:hover i {
          color: white;
        }

        /* Video Features */
        .video-features {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 24px;
          flex-wrap: wrap;
        }

        .video-feature {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 15px;
          color: #666;
          font-weight: 500;
        }

        .video-feature i {
          font-size: 22px;
          color: #E91E63;
        }

        .feature-divider {
          width: 1px;
          height: 24px;
          background: linear-gradient(to bottom, transparent, #ddd, transparent);
        }

        /* Responsive */
        @media (max-width: 991px) {
          .modern-video-section {
            padding: 60px 0;
          }

          .video-thumbnail {
            height: 400px;
          }
        }

        @media (max-width: 767px) {
          .video-thumbnail {
            height: 300px;
          }

          .play-button {
            width: 80px;
            height: 80px;
          }

          .play-button i {
            font-size: 32px;
          }

          .thumbnail-text h3 {
            font-size: 22px;
          }

          .feature-divider {
            display: none;
          }
        }
      `}</style>
    </section>
  );
};

export default VideoSection;